import { SUPABASE_STORAGE_KEY } from '@/lib/config';
import {
  getAuthAccessToken,
  refreshAuthAccessToken,
} from '@/lib/auth-bridge';

/**
 * REK-1985 (SSO.4): the subset of the session callers actually consume (the
 * bearer token for Authorization headers). Clerk is the sole engine
 * (SSO Wave 3, REK-2166) — a minimal token wrapper is returned.
 */
export interface BridgedAuthSession {
  access_token: string;
}

/**
 * Get a valid session, refreshing if necessary.
 * Returns null if no valid session can be obtained.
 * Clerk is the sole engine (SSO Wave 3, REK-2166).
 */
export async function getValidSession(): Promise<BridgedAuthSession | null> {
  // clerk-js manages token freshness internally (mints a new token when the
  // cached one nears its 60s expiry) — no manual expiry bookkeeping needed.
  const token = await getAuthAccessToken();
  return token ? { access_token: token } : null;
}

/**
 * Force refresh the session and return the new session.
 * Returns null if refresh fails.
 */
export async function forceRefreshSession(): Promise<BridgedAuthSession | null> {
  const token = await refreshAuthAccessToken();
  return token ? { access_token: token } : null;
}

/**
 * Clear stale auth storage that might cause cross-domain issues
 */
export function clearStaleAuthStorage() {
  try {
    localStorage.removeItem(SUPABASE_STORAGE_KEY);
  } catch (e) {
    // localStorage might not be available
    console.warn('Could not clear auth storage:', e);
  }
}

/**
 * Check if an error indicates a session expiry issue
 */
export function isSessionExpiredError(error: any): boolean {
  if (!error) return false;
  
  const errorMessage = error?.message || error?.error || String(error);
  const errorCode = error?.code;
  
  return (
    errorCode === 'SESSION_EXPIRED' ||
    errorCode === 'PGRST301' ||
    errorMessage.includes('expired') ||
    errorMessage.includes('invalid token') ||
    errorMessage.includes('Invalid token') ||
    errorMessage.includes('JWT expired') ||
    errorMessage.includes('session has expired')
  );
}

/**
 * Check if an error indicates auth is required
 */
export function isAuthRequiredError(error: any): boolean {
  if (!error) return false;
  
  const errorCode = error?.code;
  
  return (
    errorCode === 'AUTH_REQUIRED' ||
    errorCode === 'INVALID_TOKEN'
  );
}
